import { memo } from 'react';

import {
  AnnouncementBodyText,
  AnnouncementInlineLinks,
  type AnnouncementTone,
  visibleAnnouncementActions,
} from '@air/feature-announcements';
import { AlertBanner, type AlertBannerProps } from '@air/primitive-alert-banner';

import { useAnnouncementForSurface } from '~/components/announcements/useAnnouncementForSurface';
import { useWorkspaceAnnouncementCommands } from '~/components/announcements/useWorkspaceAnnouncementCommands';

const ALERT_BANNER_COLOR_BY_TONE: Record<AnnouncementTone, NonNullable<AlertBannerProps['color']>> = {
  error: 'red',
  info: 'blue',
  neutral: 'grey',
  warning: 'yellow',
};

export const AnnouncementGlobalBanner = memo(() => {
  const { workspaceCommandProps } = useWorkspaceAnnouncementCommands();
  const announcement = useAnnouncementForSurface({ ...workspaceCommandProps, surface: 'global' });

  if (!announcement) {
    return null;
  }

  const { icon: Icon } = announcement;
  const bodyText = announcement.body?.trim() ? announcement.body : null;

  return (
    <AlertBanner
      className="justify-start md:text-left"
      color={ALERT_BANNER_COLOR_BY_TONE[announcement.tone]}
      data-testid="ANNOUNCEMENT_GLOBAL_BANNER"
      // AlertBanner hides itself on close; keying by id stops that state carrying over to the announcement that replaces it.
      key={announcement.id}
      onDismiss={announcement.onDismiss}
      prefix={Icon ? <Icon size="sm" /> : undefined}
      // AlertBanner defaults to role="alert" (assertive), which would interrupt screen readers on every
      // page navigation; only errors warrant that.
      role={announcement.tone === 'error' ? 'alert' : 'status'}
    >
      <span className="font-semibold" data-testid="ANNOUNCEMENT_TITLE">
        {announcement.title}
      </span>
      {bodyText ? (
        <>
          {' '}
          <span data-testid="ANNOUNCEMENT_BODY">
            <AnnouncementBodyText body={bodyText} inline />
          </span>
        </>
      ) : null}
      <AnnouncementInlineLinks
        links={visibleAnnouncementActions(announcement.actions)}
        onAction={announcement.onAction}
      />
    </AlertBanner>
  );
});

AnnouncementGlobalBanner.displayName = 'AnnouncementGlobalBanner';
